import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { trpc } from '~/api/trpc';
import { paymentMethodConfig, defaultPaymentMethod } from '~/lib/payment-method-config';
import { useNavigate } from 'react-router-dom';

const paymentStatusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PENDING: { label: 'Ausstehend', variant: 'outline' },
    COMPLETED: { label: 'Abgeschlossen', variant: 'default' },
    FAILED: { label: 'Fehlgeschlagen', variant: 'destructive' },
    REFUNDED: { label: 'Erstattet', variant: 'secondary' },
};

const paymentTypeLabels: Record<string, string> = {
    BILLING_DRAFT_PAYMENT: 'Zahlung',
    BILLING_DRAFT_REFUND: 'Rückzahlung',
    INVOICE_PAYMENT: 'Rechnungszahlung',
};

type PaymentHistoryDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    customerId: number;
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

const PaymentHistoryDialog = ({
    open,
    onOpenChange,
    customerId,
}: PaymentHistoryDialogProps) => {
    const navigate = useNavigate();

    const { data, isLoading } = trpc.payments.getByCustomer.useQuery(
        { customerId, pageSize: 50 },
        { enabled: open }
    );

    const payments = data?.data ?? [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-4xl max-h-[80vh] overflow-hidden flex flex-col'>
                <DialogHeader>
                    <DialogTitle>Zahlungsverlauf</DialogTitle>
                    <DialogDescription>
                        Alle Zahlungen dieses Kunden
                    </DialogDescription>
                </DialogHeader>

                <div className='flex-1 overflow-auto'>
                    {isLoading ? (
                        <div className='space-y-2'>
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} className='h-12 w-full' />
                            ))}
                        </div>
                    ) : payments.length === 0 ? (
                        <div className='text-center py-8 text-muted-foreground'>
                            Keine Zahlungen vorhanden
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Datum</TableHead>
                                    <TableHead>Typ</TableHead>
                                    <TableHead>Methode</TableHead>
                                    <TableHead>Betrag</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Auftrag</TableHead>
                                    <TableHead>Erstellt von</TableHead>
                                    <TableHead>Notiz</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {payments.map((payment) => {
                                    const method = paymentMethodConfig[payment.paymentMethod ?? ''] ?? defaultPaymentMethod;
                                    const status = paymentStatusLabels[payment.status] ?? paymentStatusLabels.PENDING;
                                    const Icon = method.icon;
                                    const isRefund = payment.type === 'BILLING_DRAFT_REFUND';

                                    return (
                                        <TableRow key={payment.id}>
                                            <TableCell className='whitespace-nowrap'>
                                                {format(new Date(payment.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`text-sm ${isRefund ? 'text-destructive font-medium' : ''}`}>
                                                    {paymentTypeLabels[payment.type] ?? payment.type}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className='flex items-center gap-2'>
                                                    <Icon className='h-4 w-4 text-muted-foreground' />
                                                    <span className='text-sm'>{method.label}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className={`font-medium ${isRefund ? 'text-destructive' : ''}`}>
                                                {formatCurrency(payment.paidAmountCents / 100)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={status.variant}>
                                                    {status.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {payment.billingDraft ? (
                                                    <Button
                                                        variant='link'
                                                        size='sm'
                                                        className='p-0 h-auto'
                                                        onClick={() => {
                                                            onOpenChange(false);
                                                            navigate(`/billing-drafts/${payment.billingDraft?.id}`);
                                                        }}
                                                    >
                                                        {payment.billingDraft.billingDraftNumber}
                                                        <ExternalLink className='ml-1 h-3 w-3' />
                                                    </Button>
                                                ) : (
                                                    <span className='text-muted-foreground'>-</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {payment.createdByUser ? (
                                                    <span className='text-sm'>
                                                        {payment.createdByUser.firstName} {payment.createdByUser.lastName}
                                                    </span>
                                                ) : (
                                                    <span className='text-muted-foreground'>-</span>
                                                )}
                                            </TableCell>
                                            <TableCell className='max-w-[200px] truncate'>
                                                {payment.note ?? '-'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PaymentHistoryDialog;
